import { useEffect, useMemo, useState } from "react";
import { apiFetch } from "api/client";

import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";

import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";

import Card from "@mui/material/Card";
import Grid from "@mui/material/Grid";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import TextField from "@mui/material/TextField";
import MenuItem from "@mui/material/MenuItem";
import Switch from "@mui/material/Switch";
import FormControlLabel from "@mui/material/FormControlLabel";
import Chip from "@mui/material/Chip";
import Icon from "@mui/material/Icon";
import IconButton from "@mui/material/IconButton";
import Divider from "@mui/material/Divider";

const TOKEN_KEY = "solvit_auth_token";

const weekdayOptions = [
  { value: 1, label: "Mo" },
  { value: 2, label: "Di" },
  { value: 3, label: "Mi" },
  { value: 4, label: "Do" },
  { value: 5, label: "Fr" },
  { value: 6, label: "Sa" },
  { value: 0, label: "So" },
];

const emptyForm = {
  scheduleType: "ONE_TIME",
  name: "",
  description: "",
  startsAt: "",
  endsAt: "",
  timeStart: "",
  timeEnd: "",
  weekdays: [1, 2, 3, 4, 5],
  isActive: true,
  screenIds: [],
  items: [],
};

function formatDateTime(value) {
  if (!value) return "-";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "-";
  return d.toLocaleString("de-DE");
}

function mediaTypeLabel(mimeType) {
  return String(mimeType || "").startsWith("image/") ? "Bild" : "Video";
}

function normalizeWeekdays(value) {
  if (!Array.isArray(value)) return [];
  return value
    .map((v) => Number(v))
    .filter((v) => Number.isFinite(v) && v >= 0 && v <= 6)
    .map((v) => Math.trunc(v));
}

function normalizeTimeValue(value) {
  const raw = String(value || "").trim();
  if (!raw) return "";
  const match = raw.match(/^([01]\d|2[0-3]):([0-5]\d)/);
  if (!match) return "";
  return `${match[1]}:${match[2]}`;
}

export default function Campaigns() {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [info, setInfo] = useState("");

  const [campaigns, setCampaigns] = useState([]);
  const [screens, setScreens] = useState([]);
  const [media, setMedia] = useState([]);

  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editingId, setEditingId] = useState("");
  const [form, setForm] = useState(emptyForm);

  const [mediaToAdd, setMediaToAdd] = useState("");

  const mediaById = useMemo(() => {
    const map = new Map();
    media.forEach((m) => map.set(m.id, m));
    return map;
  }, [media]);

  const loadAll = async () => {
    const token = localStorage.getItem(TOKEN_KEY);
    if (!token) {
      setCampaigns([]);
      setScreens([]);
      setMedia([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    setError("");

    try {
      const [campaignRes, screenRes, mediaRes] = await Promise.all([
        apiFetch("/campaigns", { token }),
        apiFetch("/screens", { token }),
        apiFetch("/media", { token }),
      ]);

      setCampaigns(Array.isArray(campaignRes?.campaigns) ? campaignRes.campaigns : []);
      setScreens(Array.isArray(screenRes) ? screenRes : screenRes?.screens || []);
      setMedia(Array.isArray(mediaRes) ? mediaRes : mediaRes?.media || mediaRes?.items || []);
    } catch (e) {
      setError(e?.message || "Kampagnen konnten nicht geladen werden.");
      setCampaigns([]);
      setScreens([]);
      setMedia([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void loadAll();
  }, []);

  const selectedScreensLabel = useMemo(() => {
    if (!form.screenIds.length) return "Keine Bildschirme ausgewählt";
    return `${form.screenIds.length} Bildschirm(e) ausgewählt`;
  }, [form.screenIds]);

  const selectedItemsLabel = useMemo(() => `${form.items.length} Medien im Kampagnen-Loop`, [form.items]);

  const openCreate = () => {
    setEditingId("");
    setForm(emptyForm);
    setMediaToAdd("");
    setOpen(true);
  };

  const openEdit = (campaign) => {
    setEditingId(campaign.id);
    setForm({
      scheduleType: Array.isArray(campaign.weekdays) && campaign.weekdays.length > 0 ? "WEEKLY" : "ONE_TIME",
      name: campaign.name || "",
      description: campaign.description || "",
      startsAt: campaign.startsAt ? String(campaign.startsAt).slice(0, 16) : "",
      endsAt: campaign.endsAt ? String(campaign.endsAt).slice(0, 16) : "",
      timeStart: normalizeTimeValue(campaign.timeStart || ""),
      timeEnd: normalizeTimeValue(campaign.timeEnd || ""),
      weekdays: normalizeWeekdays(campaign.weekdays),
      isActive: Boolean(campaign.isActive),
      screenIds: Array.isArray(campaign.screenIds) ? campaign.screenIds : [],
      items: Array.isArray(campaign.items)
        ? campaign.items.map((item, idx) => ({
            mediaId: item.mediaId,
            durationSeconds: item.durationSeconds ?? null,
            position: item.position ?? idx,
          }))
        : [],
    });
    setMediaToAdd("");
    setOpen(true);
  };

  const closeDialog = () => {
    setOpen(false);
    setSaving(false);
    setEditingId("");
    setForm(emptyForm);
    setMediaToAdd("");
  };

  const toggleWeekday = (value) => {
    const day = Number(value);
    if (!Number.isFinite(day) || day < 0 || day > 6) return;
    setForm((prev) => ({
      ...prev,
      weekdays: prev.weekdays.includes(day)
        ? prev.weekdays.filter((v) => v !== day)
        : [...prev.weekdays, day].sort((a, b) => a - b),
    }));
  };

  const toggleScreen = (screenId) => {
    setForm((prev) => ({
      ...prev,
      screenIds: prev.screenIds.includes(screenId)
        ? prev.screenIds.filter((id) => id !== screenId)
        : [...prev.screenIds, screenId],
    }));
  };

  const addMediaToLoop = () => {
    if (!mediaToAdd) return;
    setForm((prev) => ({
      ...prev,
      items: [
        ...prev.items,
        {
          mediaId: mediaToAdd,
          position: prev.items.length,
          durationSeconds: String(mediaById.get(mediaToAdd)?.mimeType || "").startsWith("image/") ? 10 : null,
        },
      ],
    }));
    setMediaToAdd("");
  };

  const removeItem = (index) => {
    setForm((prev) => ({
      ...prev,
      items: prev.items
        .filter((_, i) => i !== index)
        .map((item, idx) => ({ ...item, position: idx })),
    }));
  };

  const moveItem = (index, direction) => {
    setForm((prev) => {
      const next = [...prev.items];
      const target = index + direction;
      if (target < 0 || target >= next.length) return prev;
      const [picked] = next.splice(index, 1);
      next.splice(target, 0, picked);
      return {
        ...prev,
        items: next.map((item, idx) => ({ ...item, position: idx })),
      };
    });
  };

  const updateItemDuration = (index, value) => {
    const parsed = Number(value);
    setForm((prev) => ({
      ...prev,
      items: prev.items.map((item, idx) =>
        idx === index
          ? {
              ...item,
              durationSeconds: Number.isFinite(parsed) ? Math.max(1, Math.trunc(parsed)) : 1,
            }
          : item
      ),
    }));
  };

  const saveCampaign = async () => {
    const normalizedWeekdays = normalizeWeekdays(form.weekdays);
    const normalizedTimeStart = normalizeTimeValue(form.timeStart);
    const normalizedTimeEnd = normalizeTimeValue(form.timeEnd);

    if (!form.name.trim()) return setError("Bitte einen Kampagnen-Namen angeben.");
    if (!form.screenIds.length) return setError("Bitte mindestens einen Bildschirm auswählen.");
    if (!form.items.length) return setError("Bitte mindestens ein Medium hinzufügen.");
    if (form.scheduleType === "ONE_TIME" && (!form.startsAt || !form.endsAt)) {
      return setError("Für eine einmalige Kampagne bitte Start und Ende (Datum + Uhrzeit) setzen.");
    }
    if (form.scheduleType === "WEEKLY" && normalizedWeekdays.length === 0) {
      return setError("Für regelmäßige Kampagnen bitte mindestens einen Wochentag auswählen.");
    }
    if (form.scheduleType === "WEEKLY" && (!normalizedTimeStart || !normalizedTimeEnd)) {
      return setError("Für regelmäßige Kampagnen bitte Uhrzeit von/bis setzen.");
    }

    const token = localStorage.getItem(TOKEN_KEY);
    if (!token) return setError("Kein Login-Token gefunden.");

    setSaving(true);
    setError("");
    setInfo("");

    try {
      const payload = {
        name: form.name.trim(),
        description: form.description.trim() || null,
        startsAt: form.scheduleType === "ONE_TIME" && form.startsAt ? new Date(form.startsAt).toISOString() : null,
        endsAt: form.scheduleType === "ONE_TIME" && form.endsAt ? new Date(form.endsAt).toISOString() : null,
        timeStart: form.scheduleType === "WEEKLY" ? (normalizedTimeStart || null) : null,
        timeEnd: form.scheduleType === "WEEKLY" ? (normalizedTimeEnd || null) : null,
        weekdays: form.scheduleType === "WEEKLY" ? normalizedWeekdays : [],
        isActive: form.isActive,
        screenIds: form.screenIds,
        items: form.items.map((item, idx) => ({
          mediaId: item.mediaId,
          position: idx,
          durationSeconds: item.durationSeconds ?? null,
        })),
      };

      if (editingId) {
        await apiFetch(`/campaigns/${editingId}`, {
          token,
          method: "PUT",
          body: JSON.stringify(payload),
        });
      } else {
        await apiFetch("/campaigns", {
          token,
          method: "POST",
          body: JSON.stringify(payload),
        });
      }

      closeDialog();
      await loadAll();
      setInfo("Kampagne gespeichert.");
    } catch (e) {
      setError(e?.message || "Kampagne konnte nicht gespeichert werden.");
    } finally {
      setSaving(false);
    }
  };

  const applyCampaignNow = async (campaignId) => {
    const token = localStorage.getItem(TOKEN_KEY);
    if (!token) return;

    setError("");
    setInfo("");
    try {
      const res = await apiFetch(`/campaigns/${campaignId}/apply`, {
        token,
        method: "POST",
      });
      setInfo(`Kampagne angewendet (${res.appliedToScreens} Bildschirm(e)).`);
      await loadAll();
    } catch (e) {
      setError(e?.message || "Kampagne konnte nicht angewendet werden.");
    }
  };

  const deleteCampaign = async (campaignId) => {
    const ok = window.confirm("Kampagne wirklich löschen?");
    if (!ok) return;

    const token = localStorage.getItem(TOKEN_KEY);
    if (!token) return;

    setError("");
    setInfo("");
    try {
      await apiFetch(`/campaigns/${campaignId}`, {
        token,
        method: "DELETE",
      });
      setInfo("Kampagne gelöscht.");
      await loadAll();
    } catch (e) {
      setError(e?.message || "Kampagne konnte nicht gelöscht werden.");
    }
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />

      <MDBox display="flex" justifyContent="space-between" alignItems="center" mb={3} mt={1}>
        <MDBox>
          <MDTypography variant="h4" fontWeight="medium">
            Kampagnen-Planer
          </MDTypography>
          <MDTypography variant="button" color="text">
            Plane zeitgesteuerte Inhalte nach Zeitraum, Wochentagen und Uhrzeiten (z.B. Halloween, Mittagskarte, Happy Hour).
          </MDTypography>
        </MDBox>

        <MDButton variant="gradient" color="info" startIcon={<Icon>event</Icon>} onClick={openCreate}>
          Neue Kampagne
        </MDButton>
      </MDBox>

      {(error || info) && (
        <MDBox mb={2}>
          {!!error && (
            <MDTypography variant="caption" color="error" display="block">
              {error}
            </MDTypography>
          )}
          {!!info && (
            <MDTypography variant="caption" color="success" display="block">
              {info}
            </MDTypography>
          )}
        </MDBox>
      )}

      {loading ? (
        <MDTypography variant="button">Lade Kampagnen...</MDTypography>
      ) : campaigns.length === 0 ? (
        <Card>
          <MDBox p={3}>
            <MDTypography variant="h6" fontWeight="medium" mb={0.5}>
              Noch keine Kampagnen
            </MDTypography>
            <MDTypography variant="button" color="text" mb={2}>
              Lege deine erste Kampagne an und plane Inhalte für bestimmte Zeiträume.
            </MDTypography>
            <MDButton variant="gradient" color="info" onClick={openCreate}>
              Kampagne anlegen
            </MDButton>
          </MDBox>
        </Card>
      ) : (
        <Grid container spacing={2}>
          {campaigns.map((campaign) => (
            <Grid item xs={12} md={6} key={campaign.id}>
              <Card>
                <MDBox p={2.2}>
                  <MDBox display="flex" justifyContent="space-between" alignItems="flex-start" gap={1} mb={1}>
                    <MDBox>
                      <MDTypography variant="h6" fontWeight="medium">
                        {campaign.name}
                      </MDTypography>
                      <MDTypography variant="caption" color="text">
                        {campaign.description || "-"}
                      </MDTypography>
                    </MDBox>
                    <Chip
                      size="small"
                      color={campaign.activeNow ? "success" : campaign.isActive ? "warning" : "default"}
                      label={campaign.activeNow ? "aktiv jetzt" : campaign.isActive ? "geplant" : "deaktiviert"}
                    />
                  </MDBox>

                  <MDTypography variant="caption" color="text" display="block">
                    Typ: {Array.isArray(campaign.weekdays) && campaign.weekdays.length > 0 ? "Regelmäßig" : "Einmalig"}
                  </MDTypography>
                  {Array.isArray(campaign.weekdays) && campaign.weekdays.length > 0 ? (
                    <>
                      <MDTypography variant="caption" color="text" display="block">
                        Tage: {campaign.weekdays.map((day) => weekdayOptions.find((w) => w.value === day)?.label || day).join(", ")}
                      </MDTypography>
                      <MDTypography variant="caption" color="text" display="block">
                        Uhrzeit: {campaign.timeStart || "-"} - {campaign.timeEnd || "-"}
                      </MDTypography>
                    </>
                  ) : (
                    <MDTypography variant="caption" color="text" display="block">
                      Zeitraum: {campaign.startsAt ? formatDateTime(campaign.startsAt) : "-"} bis {campaign.endsAt ? formatDateTime(campaign.endsAt) : "-"}
                    </MDTypography>
                  )}
                  <MDTypography variant="caption" color="text" display="block" mb={1.5}>
                    Bildschirme: {Array.isArray(campaign.screenIds) ? campaign.screenIds.length : 0} • Medien: {Array.isArray(campaign.items) ? campaign.items.length : 0}
                  </MDTypography>

                  <MDBox display="flex" gap={1} flexWrap="wrap">
                    <MDButton variant="outlined" color="info" size="small" onClick={() => openEdit(campaign)}>
                      Bearbeiten
                    </MDButton>
                    <MDButton variant="outlined" color="success" size="small" onClick={() => applyCampaignNow(campaign.id)}>
                      Jetzt anwenden
                    </MDButton>
                    <MDButton variant="outlined" color="error" size="small" onClick={() => deleteCampaign(campaign.id)}>
                      Löschen
                    </MDButton>
                  </MDBox>
                </MDBox>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      <Dialog open={open} onClose={closeDialog} fullWidth maxWidth="md">
        <DialogTitle>{editingId ? "Kampagne bearbeiten" : "Neue Kampagne"}</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 0.2 }}>
            <Grid item xs={12} md={6}>
              <TextField fullWidth label="Name" value={form.name} onChange={(e) => setForm((prev) => ({ ...prev, name: e.target.value }))} />
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControlLabel
                control={<Switch checked={form.isActive} onChange={(e) => setForm((prev) => ({ ...prev, isActive: e.target.checked }))} />}
                label="Kampagne aktiv"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                select
                label="Kampagnentyp"
                value={form.scheduleType}
                onChange={(e) => setForm((prev) => ({ ...prev, scheduleType: e.target.value }))}
              >
                <MenuItem value="ONE_TIME">Einmalig</MenuItem>
                <MenuItem value="WEEKLY">Regelmäßig</MenuItem>
              </TextField>
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth label="Beschreibung" value={form.description} onChange={(e) => setForm((prev) => ({ ...prev, description: e.target.value }))} />
            </Grid>
            {form.scheduleType === "ONE_TIME" ? (
              <>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    type="datetime-local"
                    label="Start (Datum + Uhrzeit)"
                    InputLabelProps={{ shrink: true }}
                    value={form.startsAt}
                    onChange={(e) => setForm((prev) => ({ ...prev, startsAt: e.target.value }))}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    type="datetime-local"
                    label="Ende (Datum + Uhrzeit)"
                    InputLabelProps={{ shrink: true }}
                    value={form.endsAt}
                    onChange={(e) => setForm((prev) => ({ ...prev, endsAt: e.target.value }))}
                  />
                </Grid>
              </>
            ) : (
              <>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    type="time"
                    label="Uhrzeit von"
                    InputLabelProps={{ shrink: true }}
                    value={form.timeStart}
                    onChange={(e) => setForm((prev) => ({ ...prev, timeStart: e.target.value }))}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    type="time"
                    label="Uhrzeit bis"
                    InputLabelProps={{ shrink: true }}
                    value={form.timeEnd}
                    onChange={(e) => setForm((prev) => ({ ...prev, timeEnd: e.target.value }))}
                  />
                </Grid>

                <Grid item xs={12}>
                  <MDTypography variant="caption" color="text" display="block" mb={0.8}>
                    Wochentage
                  </MDTypography>
                  <MDBox display="flex" gap={0.8} flexWrap="wrap">
                    {weekdayOptions.map((option) => (
                      <Chip
                        key={option.value}
                        label={option.label}
                        color={normalizeWeekdays(form.weekdays).includes(option.value) ? "info" : "default"}
                        onClick={() => toggleWeekday(option.value)}
                      />
                    ))}
                  </MDBox>
                </Grid>
              </>
            )}

            <Grid item xs={12}>
              <Divider sx={{ my: 1 }} />
              <MDTypography variant="caption" color="text" display="block" mb={0.8}>
                Ziel-Bildschirme ({selectedScreensLabel})
              </MDTypography>
              <Grid container spacing={1}>
                {screens.map((screen) => (
                  <Grid item xs={12} sm={6} md={4} key={screen.id}>
                    <Card variant="outlined" sx={{ borderColor: form.screenIds.includes(screen.id) ? "#1A73E8" : "#d0d7de" }}>
                      <MDBox p={1.2} display="flex" justifyContent="space-between" alignItems="center">
                        <MDTypography variant="caption">{screen.name}</MDTypography>
                        <Switch checked={form.screenIds.includes(screen.id)} onChange={() => toggleScreen(screen.id)} />
                      </MDBox>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            </Grid>

            <Grid item xs={12}>
              <Divider sx={{ my: 1 }} />
              <MDTypography variant="caption" color="text" display="block" mb={0.8}>
                Medien-Loop ({selectedItemsLabel})
              </MDTypography>

              <MDBox display="flex" gap={1} flexDirection={{ xs: "column", md: "row" }} mb={1.2}>
                <TextField
                  select
                  fullWidth
                  label="Medium hinzufügen"
                  value={mediaToAdd}
                  onChange={(e) => setMediaToAdd(e.target.value)}
                >
                  <MenuItem value="">Bitte wählen</MenuItem>
                  {media.map((m) => (
                    <MenuItem key={m.id} value={m.id}>
                      {(m.filename || m.name || m.id)} • {mediaTypeLabel(m.mimeType)}
                    </MenuItem>
                  ))}
                </TextField>
                <MDButton variant="gradient" color="info" onClick={addMediaToLoop} disabled={!mediaToAdd}>
                  Hinzufügen
                </MDButton>
              </MDBox>

              {form.items.length === 0 ? (
                <MDTypography variant="caption" color="text">
                  Noch keine Medien im Kampagnen-Loop.
                </MDTypography>
              ) : (
                <MDBox display="grid" gap={1}>
                  {form.items.map((item, index) => {
                    const m = mediaById.get(item.mediaId);
                    const isImage = String(m?.mimeType || "").startsWith("image/");
                    return (
                      <Card key={`${item.mediaId}-${index}`} variant="outlined">
                        <MDBox p={1.2} display="flex" alignItems="center" justifyContent="space-between" gap={1}>
                          <MDBox>
                            <MDTypography variant="button" fontWeight="medium">
                              {index + 1}. {m?.filename || item.mediaId}
                            </MDTypography>
                            <MDTypography variant="caption" color="text" display="block">
                              {mediaTypeLabel(m?.mimeType)}
                            </MDTypography>
                          </MDBox>

                          <MDBox display="flex" alignItems="center" gap={0.5}>
                            {isImage && (
                              <TextField
                                size="small"
                                type="number"
                                label="Sek."
                                value={item.durationSeconds ?? 10}
                                onChange={(e) => updateItemDuration(index, e.target.value)}
                                sx={{ width: 90 }}
                              />
                            )}
                            <IconButton size="small" onClick={() => moveItem(index, -1)} disabled={index === 0}>
                              <Icon>keyboard_arrow_up</Icon>
                            </IconButton>
                            <IconButton size="small" onClick={() => moveItem(index, 1)} disabled={index === form.items.length - 1}>
                              <Icon>keyboard_arrow_down</Icon>
                            </IconButton>
                            <IconButton size="small" color="error" onClick={() => removeItem(index)}>
                              <Icon>delete</Icon>
                            </IconButton>
                          </MDBox>
                        </MDBox>
                      </Card>
                    );
                  })}
                </MDBox>
              )}
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <MDButton variant="text" color="dark" onClick={closeDialog}>
            Abbrechen
          </MDButton>
          <MDButton variant="gradient" color="info" onClick={saveCampaign} disabled={saving}>
            {saving ? "Speichert..." : "Speichern"}
          </MDButton>
        </DialogActions>
      </Dialog>

      <Footer />
    </DashboardLayout>
  );
}
